import { computed } from 'vue';
import { store, genId, createDefaultRecord } from './setup_store.js';
import { scopedKey } from './setup_keys.js';
import { loadSetup, touchActiveSetup } from './state.js';

export { scopedKey };

// Read-only, derived for display (the header dropdown's <select>, the
// Setups panel table) — store.value.setups is a plain reactive object now,
// so this is always live, on any page that imports this module, with no
// manual "please re-check localStorage" nudge needed (the old setupsVersion
// existed purely to work around a non-active setup's name living behind a
// plain, non-reactive localStorage read).
const setups = computed(() =>
  Object.keys(store.value.setups).map((id) => ({ id, name: store.value.setups[id].name }))
);

const activeSetupId = computed(() => store.value.activeId);

// The remaining per-setup keys that live outside the consolidated store —
// scratch UI state for the single-mark/full-marks/graph/trajectory forms
// (see mark.vue/full_marks.vue/graph.vue/trajectory.vue). Unrelated to a
// setup's actual sight_settings/marks/calc, so there's no reason to fold
// them into the synced record; they just need cleaning up alongside it.
function deleteScopedStorage(id) {
  for (const base of ['mark-inputs', 'full-marks-inputs', 'graph-inputs', 'trajectory-inputs', 'sight-tape-inputs']) {
    localStorage.removeItem(scopedKey(base, id));
  }
}

// fallbackName only matters the first time a brand new id is loaded (see
// createSetup) — an existing setup already has a real name in its own
// record, so ordinary switches never need it.
function selectSetup(id, fallbackName) {
  if (id === store.value.activeId) return;
  store.value.activeId = id;
  loadSetup(id, fallbackName);
}

// sync.js subscribes here to push a create/rename immediately rather than
// waiting out the typing-edit debounce — like a finished compute (see
// state.js's onComputeFinished), these are single, discrete actions, not a
// burst of keystrokes. Deliberately not reused for delete, which already
// has its own listener below with different (queue-if-offline) semantics.
const changeListeners = new Set();
export function onSetupChanged(fn) {
  changeListeners.add(fn);
}

function createSetup(name) {
  const id = genId();
  store.value.setups[id] = createDefaultRecord(name);
  selectSetup(id, name);
  changeListeners.forEach((fn) => fn(id));
  return id;
}

// Renames a setup by id — a plain mutation on the store, the same for the
// active setup or any other one (no more "live inputs vs. raw localStorage"
// branch: the store IS the reactive source of truth either way now).
// updated_at is bumped directly, unconditionally, rather than only via
// touchActiveSetup() — this file's own `store` is reactive on *every* page
// that imports it, but state.js's live mirror (and the persistence watcher
// that would otherwise carry a touchActiveSetup() bump into the store) only
// exists where loadSetup() has actually run, which the separate /setups/
// management page's bundle never does (see its main.js). touchActiveSetup()
// is still called too, best-effort, for whichever page *does* have the live
// mirror open (the calculator page) — purely so a later, unrelated
// persistence-watcher write (e.g. from a recompute) doesn't overwrite this
// setup's record using its own now-stale copy of setupUpdatedAt.
function renameSetup(id, name) {
  const record = store.value.setups[id];
  record.name = name;
  record.updated_at = Date.now();
  if (id === store.value.activeId) touchActiveSetup();
  changeListeners.forEach((fn) => fn(id));
}

// sync.js subscribes here (rather than sync.js importing this file's
// deleteSetup directly and this file importing sync.js, which would be
// circular) so a local delete can be pushed to the server without this file
// knowing anything about fetch/auth. { silent: true } skips the
// notification — used when sync.js itself is the one driving the deletion (a
// setup that vanished from another device), so it doesn't turn around and
// try to delete-on-server something the server already doesn't have.
const deleteListeners = new Set();
export function onSetupDeleted(fn) {
  deleteListeners.add(fn);
}

function deleteSetup(id, { silent = false } = {}) {
  const ids = Object.keys(store.value.setups);
  if (ids.length <= 1) return false; // always keep at least one setup around
  if (!(id in store.value.setups)) return false;

  delete store.value.setups[id];
  deleteScopedStorage(id);

  if (store.value.activeId === id) selectSetup(Object.keys(store.value.setups)[0]);

  if (!silent) deleteListeners.forEach((fn) => fn(id));
  return true;
}

// Called once, synchronously, before the app mounts — resolves which setup
// should be active (falling back to the first one if the persisted id no
// longer matches anything, e.g. it was deleted in another tab) and loads
// its data into state.js's shared `inputs`/`computedResult`.
export function initSetups() {
  if (!(store.value.activeId in store.value.setups)) {
    store.value.activeId = Object.keys(store.value.setups)[0] ?? null;
  }
  loadSetup(store.value.activeId);
}

// Wipes every local setup and replaces the whole index in one go — used
// when switching identity (login, register, logout; see sync.js's
// loadFreshAfterLogin and account_widget.vue's onLogout), where nothing
// from whatever this browser had locally before should carry over or get
// merged into the new state. `newIds` is just ids — callers are expected to
// have already written each new setup's own record (including its name,
// via state.js's applySetupData) before calling this; it only swaps which
// ids are known/active and loads whichever one ends up active. An empty
// `newIds` falls back to one bare bootstrap setup — the same shape a fresh
// install starts with — so the app always has something to show.
function replaceAllSetups(newIds, activeId) {
  for (const id of Object.keys(store.value.setups)) deleteScopedStorage(id);

  if (newIds.length === 0) {
    const id = genId();
    store.value.setups = { [id]: createDefaultRecord('Default') };
    store.value.activeId = id;
  } else {
    const kept = {};
    for (const id of newIds) kept[id] = store.value.setups[id];
    store.value.setups = kept;
    store.value.activeId = activeId;
  }
  loadSetup(store.value.activeId);
}

export function useSetups() {
  return { setups, activeSetupId, createSetup, deleteSetup, selectSetup, renameSetup, replaceAllSetups };
}
